import {AnyAction, DocumentExceededMaximumFirestoreDepthError, IDocumentData, UnknownError} from "@buildwithflux/core";
import {isDevEnv} from "@buildwithflux/shared";
import {defer, EMPTY, Subject, Subscription} from "rxjs";
import {catchError, debounceTime, filter, mergeMap, switchMap, takeUntil, tap} from "rxjs/operators";

import {DocumentStatusStates} from "modules/app/state/app";
import type {AppStore} from "modules/app/state/app";
import type {DocumentStore} from "modules/document/state/document";
import {useFeatureFlags} from "modules/feature_flags/hooks";
import {FluxLogger} from "modules/storage_engine/connectors/LogConnector";
import type {DocumentStorage} from "modules/storage_engine/DocumentStorage";
import {createActionDiscriminator} from "modules/document/state/helpers/batchActions";
import {writeDocument} from "modules/document/state/actionCreators/documentActions";
import R from "resources/Namespace";

const isDocumentDepthError = (error: string) => {
    const isDepthError = error.includes("contains an invalid nested entity");
    if (isDepthError) {
        FluxLogger.captureError(new DocumentExceededMaximumFirestoreDepthError());
    }
    return isDepthError;
};

/**
 * Service that handles document saves.
 *
 * This service replaces the Redux epic `saveDocumentEpic` using Zustand for state management.
 * It uses RxJS internally for action stream processing while fitting into the FluxServices DI pattern.
 *
 * The service subscribes to an action stream and saves document data when
 * writeDocument actions are dispatched, with debouncing to avoid excessive writes.
 *
 * On success, it updates the Zustand app store to reflect the synced status.
 * On error, it updates the Zustand app store to show error notifications and status.
 */
export interface DocumentSaveService {
    /**
     * Called when an action is dispatched to the Redux store.
     * The service filters and processes relevant actions internally.
     */
    onAction: (action: AnyAction) => void;

    /**
     * Disposes of all subscriptions and cleans up resources.
     * Should be called when the service is no longer needed.
     */
    dispose: () => void;
}

export interface DocumentSaveServiceDependencies {
    documentStorage: DocumentStorage;
    appStore: AppStore;
    /**
     * The Zustand document store for reading document state.
     */
    documentStore: DocumentStore;
}

/**
 * Creates a DocumentSaveService instance.
 *
 * This factory function creates a service that handles document saves
 * using RxJS for action stream processing with debouncing.
 *
 * @param dependencies - The service dependencies (documentStorage, appStore, documentStore)
 * @returns A DocumentSaveService instance
 */
export function createDocumentSaveService(dependencies: DocumentSaveServiceDependencies): DocumentSaveService {
    const {documentStorage, appStore, documentStore} = dependencies;
    const action$ = new Subject<AnyAction>();
    const destroy$ = new Subject<void>();
    const subscriptions: Subscription[] = [];
    let disposed = false;

    // Save document epic replacement
    // Handles saving document data to Firestore with debouncing
    const saveDocumentSubscription = action$
        .pipe(
            takeUntil(destroy$),
            filter(createActionDiscriminator(writeDocument)),
            filter(
                (action) =>
                    !(useFeatureFlags.getState().disableAutoSave ?? false) || action.payload.overrideFeatureFlag,
            ),
            // NOTE: we debounce saves here instead of in makeGenerateActionRecordsEpic because
            // document writes may be dropped for the next snapshot while should always save
            // action records, which are essentially diffs
            // NOTE: this delay has implications for our consistency model: we don't currently do
            // optimistic concurrency locking or versioning on document writes, so part of the
            // reason to use a large debounce time here is to provide more time for conflicting
            // state to be locally merged via the action records mechanism.
            debounceTime(R.behaviors.storage.writeMaxWait),
            // Set "Saving..." status right before save starts (after debounce period completes)
            tap(() => {
                const timestamp = new Date().getTime();
                appStore.getState().setDocumentStatus(DocumentStatusStates.syncing, timestamp);
            }),
            // NOTE: switchMap will drop previous observables for later observables,
            // but the firestore query is not cancelled
            switchMap((action) => {
                const documentState = action.payload.documentState as IDocumentData;
                const saveStartInMs = Date.now();

                return defer(() =>
                    documentStorage.saveDocumentData(documentState).catch((error) => {
                        // Log at promise level to ensure rejection is always handled,
                        // even if the RxJS subscription has been torn down
                        FluxLogger.captureError(
                            new UnknownError(
                                `Error in DocumentSaveService (promise-level): ${error}`,
                                error instanceof Error ? error : undefined,
                            ),
                        );
                        // Rethrow to let the RxJS catchError handle it if subscription is still active
                        throw error;
                    }),
                ).pipe(
                    mergeMap(() => {
                        if (isDevEnv()) {
                            // eslint-disable-next-line no-console
                            console.info(`FLUX-PERF: saveDocumentData took ${Date.now() - saveStartInMs}ms`);
                        }

                        // Update Zustand app store directly (no Redux dispatch needed)
                        const appState = appStore.getState();
                        const syncedTimestamp = new Date().getTime();

                        // Remove network error notification
                        appState.removeNotification("network_error");

                        // Set synced status
                        appState.setDocumentStatus(DocumentStatusStates.synced, syncedTimestamp);

                        // Hide editor backdrop if currently shown
                        if (appState.showEditorBackdrop) {
                            appState.toggleEditorBackdrop(false);
                        }

                        // Set reconnecting to false if currently reconnecting
                        if (appState.isReconnecting) {
                            appState.setIsReconnecting(false);
                        }

                        // Return EMPTY since we're handling everything via Zustand
                        return EMPTY;
                    }),
                    catchError((error: Error) => {
                        // QUESTION: why dynamic import here?
                        const NetworkErrorSnackbar =
                            require("components/common/components/Snackbars/NetworkError").default;
                        const SavingErrorSnackbar =
                            require("components/common/components/Snackbars/SavingError").default;
                        FluxLogger.captureError(
                            new UnknownError(`Error in DocumentSaveService processing action: ${error}`, error),
                        );

                        // Update Zustand app store directly (no Redux dispatch needed)
                        const appState = appStore.getState();
                        const errorTimestamp = new Date().getTime();

                        // Set error status
                        appState.setDocumentStatus(DocumentStatusStates.error, errorTimestamp, "Failed to save");

                        // Show editor backdrop
                        appState.toggleEditorBackdrop(true);

                        // Set reconnecting to false
                        appState.setIsReconnecting(false);

                        // Check if there's already a network error notification
                        const hasExistingNetworkError =
                            appState.notifications.length > 0 &&
                            !!appState.notifications.find((notification) => {
                                return !notification.dismiss && notification.key === "network_error";
                            });

                        // Enqueue network error notification if not already present
                        if (!hasExistingNetworkError) {
                            appState.enqueueNotification("", "project", {
                                preventDuplicate: true,
                                autoHideDuration: null,
                                key: "network_error",
                                content: isDocumentDepthError(error.message)
                                    ? SavingErrorSnackbar
                                    : NetworkErrorSnackbar,
                            });
                        }

                        // Return EMPTY since we're handling everything via Zustand
                        return EMPTY;
                    }),
                );
            }),
            // Top-level catchError to prevent any unexpected errors from crashing the process
            catchError((error) => {
                FluxLogger.captureError(
                    new UnknownError(`Unexpected error in document save stream: ${JSON.stringify(error)}`, error),
                );
                return EMPTY;
            }),
        )
        .subscribe({
            // No actions to dispatch - everything is handled via Zustand
            next: () => {},
            error: (error) => {
                FluxLogger.captureError(
                    new UnknownError(`Document save subscription error: ${JSON.stringify(error)}`, error),
                );
            },
        });
    subscriptions.push(saveDocumentSubscription);

    const service: DocumentSaveService = {
        onAction: (action: AnyAction) => {
            if (disposed) {
                return;
            }
            action$.next(action);
        },
        dispose: () => {
            disposed = true;
            destroy$.next();
            destroy$.complete();
            for (const subscription of subscriptions) {
                subscription.unsubscribe();
            }
            action$.complete();
        },
    };

    return service;
}
